import { format, parse } from 'date-fns';
import { Chat, Message } from '../types';

interface ParsedLine {
  timestamp?: string;
  sender?: string;
  text: string;
  isMessage: boolean;
}

/**
 * WhatsApp message patterns - supports multiple formats:
 * 1. 12:16pm, 12 Apr - Nish: Message
 * 2. 6/14/15, 8:54 AM - Kuposhit: Message
 * 3. 20/04/2014, 9:27 PM - Rabdi: Message
 */
const MESSAGE_PATTERNS = [
  // Format: 12:16pm, 12 Apr - Sender: Message
  /^(\d{1,2}:\d{2}\s*(?:AM|PM|am|pm),\s+\d{1,2}\s+[A-Za-z]{3})\s*-\s*([^:]+):\s*(.*)$/,
  // Format: 6/14/15, 8:54 AM - Sender: Message (with slashes)
  /^(\d{1,2}\/\d{1,2}\/\d{2,4},\s+\d{1,2}:\d{2}\s+(?:AM|PM|am|pm))\s*-\s*([^:]+):\s*(.*)$/,
  // Format without AM/PM: 6/14/15, 8:54 - Sender: Message
  /^(\d{1,2}\/\d{1,2}\/\d{2,4},\s+\d{1,2}:\d{2})\s*-\s*([^:]+):\s*(.*)$/,
];

/**
 * Facebook/Pidgin chat patterns:
 * Session Start (email:Name): Day Month DD HH:MM:SS YYYY timezone
 * Name: Message
 */
const FB_SESSION_START = /^Session Start \([^:]+:([^)]+)\):\s+(.+?)(?:\s+\d{6})?\s*$/;
const FB_SESSION_CLOSE = /^Session Close/;
const FB_MESSAGE = /^([^:]+):\s*(.*)$/;

export class WhatsAppParser {
  private myNames: Set<string>;
  private timestampCache: Map<string, Date | null>;

  constructor(myNames: string[] = []) {
    this.myNames = new Set(myNames.map((n) => n.toLowerCase().trim()));
    this.timestampCache = new Map();
  }

  /**
   * Parse a single line to check if it's a new message or continuation
   */
  private parseLine(line: string): ParsedLine {
    // Try each pattern until we find a match
    for (const pattern of MESSAGE_PATTERNS) {
      const match = line.match(pattern);
      if (match) {
        const [, timestamp, sender, text] = match;
        return {
          timestamp: timestamp.trim(),
          sender: sender.trim(),
          text: text,
          isMessage: true,
        };
      }
    }

    return {
      text: line,
      isMessage: false,
    };
  }

  /**
   * Parse timestamp string to Date object (with caching)
   */
  private parseTimestamp(timestampStr: string): Date | null {
    // Check cache first
    if (this.timestampCache.has(timestampStr)) {
      return this.timestampCache.get(timestampStr)!;
    }

    try {
      // Support multiple WhatsApp timestamp formats
      const formats = [
        // DD/MM or MM/DD with 2-digit year (most common, try first)
        'd/M/yy, h:mm a',
        'dd/MM/yy, h:mm a',
        'd/M/yy, hh:mm a',
        'dd/MM/yy, hh:mm a',
        // DD/MM or MM/DD with 4-digit year
        'd/M/yyyy, h:mm a',
        'dd/MM/yyyy, h:mm a',
        'd/M/yyyy, hh:mm a',
        'dd/MM/yyyy, hh:mm a',
        // Time-first format: "12:16pm, 12 Apr"
        'h:mma, d MMM',
        'h:mm a, d MMM',
        'hh:mma, dd MMM',
        'hh:mm a, dd MMM',
        // American format
        'M/d/yy, h:mm a',
        'MM/dd/yy, h:mm a',
        'M/d/yyyy, h:mm a',
        'MM/dd/yyyy, h:mm a',
        // Formats without AM/PM (24-hour)
        'd/M/yy, H:mm',
        'dd/MM/yy, HH:mm',
        'd/M/yyyy, H:mm',
        'dd/MM/yyyy, HH:mm',
      ];

      for (const formatStr of formats) {
        try {
          const date = parse(timestampStr, formatStr, new Date());
          if (!isNaN(date.getTime())) {
            this.timestampCache.set(timestampStr, date);
            return date;
          }
        } catch {
          continue;
        }
      }

      this.timestampCache.set(timestampStr, null);
      return null;
    } catch (error) {
      console.warn('Failed to parse timestamp:', timestampStr, error);
      this.timestampCache.set(timestampStr, null);
      return null;
    }
  }

  /**
   * Check if sender is the user
   */
  private isFromMe(sender: string | null): boolean {
    if (!sender) return false;
    return this.myNames.has(sender.toLowerCase().trim());
  }

  /**
   * Generate unique ID
   */
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Parse a WhatsApp chat file content (or Facebook chat if detected)
   */
  public parseChat(content: string, fileName: string, chatId: string): Chat {
    // Detect if this is a Facebook/Pidgin chat
    const hasSessions = content.includes('Session Start');
    const hasSessionMatch = content.match(FB_SESSION_START);
    
    console.log(`Parsing ${fileName}:`);
    console.log(`  - Has "Session Start": ${hasSessions}`);
    console.log(`  - Matches FB pattern: ${hasSessionMatch !== null}`);
    
    if (hasSessions && hasSessionMatch) {
      console.log(`  -> Using Facebook parser`);
      return this.parseFacebookChat(content, fileName, chatId);
    }
    
    console.log(`  -> Using WhatsApp parser`);
    
    // Parse as WhatsApp format
    const lines = content.split('\n');
    const messages: Message[] = [];
    const participants = new Set<string>();
    
    let currentMessage: Partial<Message> | null = null;
    let lineNumber = 0;

    for (const line of lines) {
      lineNumber++;
      
      // Skip empty lines between messages
      if (!line.trim() && !currentMessage) {
        continue;
      }

      const parsed = this.parseLine(line);

      if (parsed.isMessage && parsed.timestamp && parsed.sender) {
        // Save previous message if exists
        if (currentMessage) {
          messages.push(currentMessage as Message);
        }

        // Start new message
        const date = this.parseTimestamp(parsed.timestamp);
        const timestamp = date ? date.toISOString() : new Date().toISOString();
        const unixMs = date ? date.getTime() : 0;
        const dateKey = date ? format(date, 'yyyy-MM-dd') : '';

        participants.add(parsed.sender);

        currentMessage = {
          id: this.generateId(),
          chatId,
          sender: parsed.sender,
          text: parsed.text,
          rawText: parsed.text,
          timestampRaw: parsed.timestamp,
          timestamp,
          unixMs,
          dateKey,
          isFromMe: this.isFromMe(parsed.sender),
          parseWarnings: date === null ? ['Failed to parse timestamp'] : undefined,
        };
      } else {
        // Continuation of previous message or unparseable line
        if (currentMessage) {
          currentMessage.text += '\n' + parsed.text;
          currentMessage.rawText += '\n' + parsed.text;
        } else {
          // Orphan line at start of file - create a message without sender
          currentMessage = {
            id: this.generateId(),
            chatId,
            sender: null,
            text: parsed.text,
            rawText: parsed.text,
            timestampRaw: '',
            timestamp: new Date().toISOString(),
            unixMs: 0,
            dateKey: '',
            isFromMe: false,
            parseWarnings: ['Line without timestamp at start of file'],
          };
        }
      }
    }

    // Save last message
    if (currentMessage) {
      messages.push(currentMessage as Message);
    }

    // Extract chat title from filename
    const title = fileName.replace(/\.txt$/i, '').trim() || 'Untitled Chat';

    // Calculate date range
    const timestamps = messages.map((m) => m.unixMs).filter((t) => t > 0);
    const firstMessageDate = timestamps.length > 0 ? Math.min(...timestamps) : undefined;
    const lastMessageDate = timestamps.length > 0 ? Math.max(...timestamps) : undefined;

    return {
      id: chatId,
      title,
      sourceFileName: fileName,
      participants: Array.from(participants),
      messageCount: messages.length,
      messages,
      firstMessageDate,
      lastMessageDate,
    };
  }

  /**
   * Parse Facebook/Pidgin chat format
   * Format: Session Start (email:Name): Day Month DD HH:MM:SS YYYY timezone
   *         Name: message
   */
  private parseFacebookChat(content: string, fileName: string, chatId: string): Chat {
    console.log(`Parsing ${fileName} as Facebook chat format`);
    
    // Pre-process: Fix line breaks in Session Start lines
    // Sometimes "2011" is split across lines as "201" + newline + "1 051800"
    content = content.replace(/(\d{3})\s*\n\s*(\d\s+\d{6})/g, '$1$2');
    
    const lines = content.split('\n');
    const messages: Message[] = [];
    const participants = new Set<string>();
    
    let sessionTimestamp: Date | null = null;
    let messageCounter = 0; // Counter to offset timestamps within a session
    let lastMessage: Message | null = null;

    for (let line of lines) {
      // Sometimes multiple messages are on same line separated by many spaces
      // Split them if we detect 50+ consecutive spaces
      const splitLines = line.split(/\s{50,}/);
      
      for (const part of splitLines) {
        const trimmed = part.trim();
        
        // Skip empty lines and header text
        if (!trimmed || trimmed.startsWith('this history') || trimmed.startsWith('almost') || 
            trimmed.startsWith('so enjoy') || trimmed.startsWith('and plzzz') || 
            trimmed.startsWith('agar tu') || trimmed.includes('signed off at')) {
          continue;
        }

        // Session start - capture timestamp
        const sessionMatch = trimmed.match(FB_SESSION_START);
        if (sessionMatch) {
          const dateStr = sessionMatch[2];
          // Parse: Mon Jan 31 14:49:30 2011
          try {
            sessionTimestamp = parse(dateStr, 'EEE MMM dd HH:mm:ss yyyy', new Date());
            messageCounter = 0; // Reset counter for new session
            lastMessage = null;
          } catch (e) {
            sessionTimestamp = new Date();
            messageCounter = 0;
            lastMessage = null;
          }
          continue;
        }

        // Session close
        if (FB_SESSION_CLOSE.test(trimmed)) {
          sessionTimestamp = null;
          messageCounter = 0;
          lastMessage = null;
          continue;
        }

        // Message line
        const msgMatch = trimmed.match(FB_MESSAGE);
        if (msgMatch && sessionTimestamp) {
          const sender = msgMatch[1].trim();
          const text = msgMatch[2].trim();
          
          // Skip if no text (sometimes there are lines like "Priyanka Jathar:")
          if (!text) {
            continue;
          }

          // Create new message with incremented timestamp
          participants.add(sender);
          
          // Add seconds to timestamp to ensure each message has unique time
          const msgTimestamp = new Date(sessionTimestamp.getTime() + (messageCounter * 1000));
          messageCounter++;
          
          const timestamp = msgTimestamp.toISOString();
          const unixMs = msgTimestamp.getTime();
          const dateKey = format(msgTimestamp, 'yyyy-MM-dd');

          const message: Message = {
            id: this.generateId(),
            chatId,
            sender,
            text,
            rawText: text,
            timestampRaw: msgTimestamp.toString(),
            timestamp,
            unixMs,
            dateKey,
            isFromMe: this.isFromMe(sender),
          };
          
          messages.push(message);
          lastMessage = message;
        } else if (lastMessage && sessionTimestamp && trimmed && !FB_SESSION_CLOSE.test(trimmed)) {
          // Line doesn't match message pattern - might be continuation of previous message
          // Append to last message
          lastMessage.text += '\n' + trimmed;
          lastMessage.rawText += '\n' + trimmed;
        }
      }
    }

    const title = fileName.replace(/\.txt$/i, '').trim() || 'Untitled Chat';
    const timestamps = messages.map((m) => m.unixMs).filter((t) => t > 0);
    const firstMessageDate = timestamps.length > 0 ? Math.min(...timestamps) : undefined;
    const lastMessageDate = timestamps.length > 0 ? Math.max(...timestamps) : undefined;

    console.log(`Parsed ${messages.length} messages from ${fileName}`);
    console.log(`Participants: ${Array.from(participants).join(', ')}`);

    return {
      id: chatId,
      title,
      sourceFileName: fileName,
      participants: Array.from(participants),
      messageCount: messages.length,
      messages,
      firstMessageDate,
      lastMessageDate,
    };
  }

  /**
   * Parse multiple chat files with better performance for large files
   */
  public async parseFiles(
    files: File[],
    onProgress?: (current: number, total: number, fileName: string) => void
  ): Promise<Chat[]> {
    const chats: Chat[] = [];

    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      if (onProgress) {
        onProgress(i + 1, files.length, file.name);
      }

      try {
        // For large files (>1MB), use chunked reading for better performance
        const content = await this.readFileAsText(file);
        const chatId = this.generateId();
        
        // Parse in small batches to avoid blocking UI
        const chat = await this.parseChatlarge(content, file.name, chatId);
        chats.push(chat);
        
        // Give browser a chance to breathe between files
        await new Promise(resolve => setTimeout(resolve, 0));
      } catch (error) {
        console.error(`Failed to parse file ${file.name}:`, error);
      }
    }

    return chats;
  }

  /**
   * Parse large chat files efficiently
   */
  private async parseChatlarge(content: string, fileName: string, chatId: string): Promise<Chat> {
    // For very large files, process in chunks
    if (content.length > 1000000) { // 1MB
      return this.parseChunked(content, fileName, chatId);
    }
    return this.parseChat(content, fileName, chatId);
  }

  /**
   * Parse file in chunks to avoid blocking
   */
  private async parseChunked(content: string, fileName: string, chatId: string): Promise<Chat> {
    const lines = content.split('\n');
    const messages: Message[] = [];
    const participants = new Set<string>();
    
    let currentMessage: Partial<Message> | null = null;
    const CHUNK_SIZE = 1000; // Process 1000 lines at a time
    
    for (let i = 0; i < lines.length; i += CHUNK_SIZE) {
      const chunk = lines.slice(i, Math.min(i + CHUNK_SIZE, lines.length));
      
      for (const line of chunk) {
        if (!line.trim() && !currentMessage) {
          continue;
        }

        const parsed = this.parseLine(line);

        if (parsed.isMessage && parsed.timestamp && parsed.sender) {
          if (currentMessage) {
            messages.push(currentMessage as Message);
          }

          const date = this.parseTimestamp(parsed.timestamp);
          const timestamp = date ? date.toISOString() : new Date().toISOString();
          const unixMs = date ? date.getTime() : 0;
          const dateKey = date ? format(date, 'yyyy-MM-dd') : '';

          participants.add(parsed.sender);

          currentMessage = {
            id: this.generateId(),
            chatId,
            sender: parsed.sender,
            text: parsed.text,
            rawText: parsed.text,
            timestampRaw: parsed.timestamp,
            timestamp,
            unixMs,
            dateKey,
            isFromMe: this.isFromMe(parsed.sender),
            parseWarnings: date === null ? ['Failed to parse timestamp'] : undefined,
          };
        } else {
          if (currentMessage) {
            currentMessage.text += '\n' + parsed.text;
            currentMessage.rawText += '\n' + parsed.text;
          } else {
            currentMessage = {
              id: this.generateId(),
              chatId,
              sender: null,
              text: parsed.text,
              rawText: parsed.text,
              timestampRaw: '',
              timestamp: new Date().toISOString(),
              unixMs: 0,
              dateKey: '',
              isFromMe: false,
              parseWarnings: ['Line without timestamp at start of file'],
            };
          }
        }
      }
      
      // Yield to browser every chunk
      if (i + CHUNK_SIZE < lines.length) {
        await new Promise(resolve => setTimeout(resolve, 0));
      }
    }

    if (currentMessage) {
      messages.push(currentMessage as Message);
    }

    const title = fileName.replace(/\.txt$/i, '').trim() || 'Untitled Chat';
    const timestamps = messages.map((m) => m.unixMs).filter((t) => t > 0);
    const firstMessageDate = timestamps.length > 0 ? Math.min(...timestamps) : undefined;
    const lastMessageDate = timestamps.length > 0 ? Math.max(...timestamps) : undefined;

    return {
      id: chatId,
      title,
      sourceFileName: fileName,
      participants: Array.from(participants),
      messageCount: messages.length,
      messages,
      firstMessageDate,
      lastMessageDate,
    };
  }

  /**
   * Read file as text with UTF-8 encoding
   */
  private readFileAsText(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        const result = e.target?.result;
        if (typeof result === 'string') {
          resolve(result);
        } else {
          reject(new Error('Failed to read file as text'));
        }
      };
      
      reader.onerror = () => {
        reject(reader.error);
      };
      
      reader.readAsText(file, 'UTF-8');
    });
  }
}
